import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import type { inferRouterOutputs } from "@trpc/server";
import { UserCog } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuth, type AdminRole } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { SearchField } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { BUILT_IN_ORGANISER_LABEL, roleDisplayLabel } from "../lib/labels";
import { trpc } from "../trpc";

import {
  INVITE_NAME_FIELD_ID,
  INVITE_SECTION_ID,
  InviteTeam,
} from "./roles/InviteTeam";

/**
 * Team access: who can sign in to the team app and at what level.
 * Organisers only.
 *
 * Every data and door procedure on the backend is organiser-only, so the
 * page says plainly that anyone running Reception or the guest list needs
 * to be an organiser. Each person has one "Change access" panel that
 * preselects their current level and keeps Save disabled until the choice
 * differs, so opening it and tapping Save can never demote anyone.
 */

type PrivilegedUser =
  inferRouterOutputs<AppRouter>["adminListPrivilegedUsers"]["users"][number];

const HAS_MEMBERS = CHAPTER.features.includes("members");
const HAS_FEEDBACK = CHAPTER.features.includes("feedback");

/** A level Team access can give. The retired coordinator role is not one:
 *  an account still holding it is shown, and changed, as a team member. */
type AccessRole = Exclude<AdminRole, "coordinator">;

/** One level on offer. `value: null` is no access at all. */
interface AccessLevel {
  value: AccessRole | null;
  /** Heading for the group of people at this level. */
  groupTitle: string;
  description: string;
}

const ALL_ACCESS_LEVELS: AccessLevel[] = [
  {
    value: "superadmin",
    groupTitle: "Organisers",
    description: HAS_MEMBERS
      ? "Full access: sees every guest, member and website message, runs Reception, and can change who has access."
      : "Full access: sees every guest and website message, runs Reception, and can change who has access.",
  },
  {
    value: "volunteer",
    groupTitle: "Team members",
    description: HAS_FEEDBACK
      ? "Can post on the Feedback board, but can't see any guest details."
      : "Can sign in, but can't see any guest or member details.",
  },
  {
    value: "member",
    groupTitle: "Members",
    description:
      "Sees only their own membership card. This is for society members, not the team.",
  },
  {
    value: null,
    groupTitle: "No access",
    description: "Can't open anything until someone gives them access.",
  },
];

/** The levels this chapter offers: no membership card without Members. */
const ACCESS_LEVELS = ALL_ACCESS_LEVELS.filter(
  (level) => level.value !== "member" || HAS_MEMBERS,
);

/**
 * The level a stored role amounts to on this chapter. An account still
 * holding the retired coordinator role has exactly a team member's access,
 * and a member role where there is no society membership opens nothing.
 */
function levelOf(role: AdminRole | null): AccessRole | null {
  if (role === "coordinator") {
    return "volunteer";
  }
  if (role === "member" && !HAS_MEMBERS) {
    return null;
  }
  return role;
}

/** "{name} will be …" once a different level is picked. */
const WILL_BE = {
  superadmin: "will be an organiser.",
  volunteer: "will be a team member.",
  member: "will only see their membership card.",
} satisfies Record<AccessRole, string>;

/** "{name} is now …" after a save. */
const IS_NOW = {
  superadmin: "is now an organiser.",
  volunteer: "is now a team member.",
  member: "can now see their membership card.",
} satisfies Record<AccessRole, string>;

// Backgrounds are mixed FROM the ink/amber tokens (rather than a fixed
// rgba) so they track light/dark mode automatically.
const LEVEL_TONE = {
  member: {
    bg: "color-mix(in srgb, var(--color-ink) 10%, transparent)",
    color: "var(--color-ink-soft)",
  },
  volunteer: {
    bg: "color-mix(in srgb, var(--color-amber) 20%, transparent)",
    color: "var(--color-ink)",
  },
  superadmin: { bg: "var(--color-amber)", color: "var(--color-paper)" },
} satisfies Record<AccessRole, { bg: string; color: string }>;

const NO_ACCESS_TONE = {
  bg: "transparent",
  color: "var(--color-danger)",
};

/** The team's own sentence for the two refusals adminSetRole can give;
 *  anything else goes through the app-wide mapping. */
function roleErrorSentence(message: string, otherwise: string): string {
  if (message.startsWith("Promote another superadmin")) {
    return "You're the only organiser. Make someone else an organiser first.";
  }
  if (/SUPERADMIN_EMAILS/.test(message)) {
    return "This person is a built-in organiser, so their access can't be changed here. Ask Adi to change it.";
  }
  return otherwise;
}

function firstNameOf(user: PrivilegedUser): string {
  const name = user.fullName?.trim();
  if (name) {
    return name.split(/\s+/)[0] ?? name;
  }
  return user.email ?? "This person";
}

const buttonBase =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-label font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";

export function Roles() {
  const { user: currentUser } = useAuth();
  const utils = trpc.useUtils();
  const query = trpc.adminListPrivilegedUsers.useQuery(undefined, {
    retry: false,
  });

  /** Whose Change access panel is open. One at a time. */
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [choice, setChoice] = useState<AccessRole | null>(null);
  const [panelError, setPanelError] = useState<{
    userId: string;
    message: string;
  } | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const setRole = trpc.adminSetRole.useMutation({
    onSuccess: (_data, vars) => {
      const person = query.data?.users.find(
        (u) => u.userId === vars.targetUserId,
      );
      const name = person ? firstNameOf(person) : "This person";
      setSavedNote(
        vars.role === null
          ? `Saved. ${name} no longer has access.`
          : `Saved. ${name} ${IS_NOW[vars.role]}`,
      );
      setOpenFor(null);
      setPanelError(null);
      void utils.adminListPrivilegedUsers.invalidate();
      void utils.adminMe.invalidate();
    },
    onError: (err, vars) => {
      setPanelError({
        userId: vars.targetUserId,
        message: roleErrorSentence(err.message, friendlyError(err)),
      });
    },
  });

  const bootstrapEmails = useMemo(
    () => query.data?.bootstrapEmails ?? [],
    [query.data],
  );

  /** Built in: on the install-time email list, whatever Clerk says. Both
   *  sides lowercased, because Clerk keeps the case people typed. */
  function isBuiltIn(u: PrivilegedUser): boolean {
    return (
      u.isBootstrap ||
      (u.email !== null && bootstrapEmails.includes(u.email.toLowerCase()))
    );
  }

  const users = useMemo(() => query.data?.users ?? [], [query.data]);
  const explicitOrganisers = users.filter(
    (u) =>
      u.role === "superadmin" &&
      !u.isBootstrap &&
      !(u.email !== null && bootstrapEmails.includes(u.email.toLowerCase())),
  ).length;

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const shown = q
      ? users.filter((u) =>
          `${u.fullName ?? ""} ${u.email ?? ""}`.toLowerCase().includes(q),
        )
      : users;
    return ACCESS_LEVELS.map((level) => ({
      level,
      people: shown.filter((u) => levelOf(u.role) === level.value),
    })).filter((group) => group.people.length > 0);
  }, [users, search]);

  function openPanel(u: PrivilegedUser) {
    setOpenFor(u.userId);
    setChoice(levelOf(u.role));
    setPanelError(null);
    setSavedNote(null);
  }

  function chooseLevel(value: AccessRole | null) {
    setChoice(value);
    setPanelError(null);
  }

  function save(u: PrivilegedUser) {
    setRole.mutate({ targetUserId: u.userId, role: choice });
  }

  function jumpToInvite() {
    document
      .getElementById(INVITE_SECTION_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    document
      .getElementById(INVITE_NAME_FIELD_ID)
      ?.focus({ preventScroll: true });
  }

  return (
    <section className="mx-auto max-w-4xl px-5 py-10 md:px-10 md:py-12">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 border-b border-[color:var(--color-line)] pb-6">
        <div className="min-w-0 flex-1">
          <h1 className="page-title">Team access</h1>
          <p className="mt-3 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
            Everyone who can sign in to {CHAPTER.adminHost} is listed here.
            {HAS_MEMBERS
              ? " Only organisers can see guests, run Reception, work on members or read messages from the website."
              : " Only organisers can see guests, run Reception or read messages from the website."}
          </p>
        </div>
        <button
          type="button"
          onClick={jumpToInvite}
          className={`${buttonBase} bg-[color:var(--color-ink)] text-[color:var(--color-paper)] hover:opacity-85`}
        >
          Invite someone
        </button>
      </header>

      <section
        aria-labelledby="access-levels-heading"
        className="surface mt-8 p-5 md:p-6"
      >
        <h2
          id="access-levels-heading"
          className="text-title font-bold text-[color:var(--color-ink)]"
        >
          What each level means
        </h2>
        <dl className="mt-4 flex flex-col gap-3">
          {ACCESS_LEVELS.map((level) => (
            <div
              key={level.value ?? "none"}
              className="flex flex-col gap-0.5 sm:flex-row sm:gap-4"
            >
              <dt className="shrink-0 text-body font-semibold text-[color:var(--color-ink)] sm:w-36">
                {roleDisplayLabel(level.value, CHAPTER.id)}
              </dt>
              <dd className="text-body text-[color:var(--color-ink-soft)]">
                {level.description}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-10">
        {query.isLoading ? (
          <p className="text-body text-[color:var(--color-ink-soft)]">
            Loading the team…
          </p>
        ) : query.error ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-body text-[color:var(--color-danger)]">
              Couldn&apos;t load the list of people.{" "}
              {friendlyError(
                query.error,
                "Check your internet connection and try again.",
              )}
            </p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className={`${buttonBase} border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]`}
            >
              Try again
            </button>
          </div>
        ) : users.length === 0 ? (
          <p className="empty-state">Nobody has been given access yet.</p>
        ) : (
          <>
            {users.length > 8 ? (
              <div className="mb-6 flex">
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name or email"
                />
              </div>
            ) : null}

            {savedNote ? (
              <p
                role="status"
                className="mb-6 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-paper)] px-4 py-3 text-body text-[color:var(--color-ink)]"
              >
                {savedNote}
              </p>
            ) : null}

            {groups.length === 0 ? (
              <p className="empty-state">Nobody matches that search.</p>
            ) : (
              <div className="flex flex-col gap-10">
                {groups.map(({ level, people }) => (
                  <section key={level.value ?? "none"}>
                    <h2 className="text-title font-bold text-[color:var(--color-ink)]">
                      {level.groupTitle} ({people.length})
                    </h2>
                    <ul className="mt-3 flex flex-col gap-3">
                      {people.map((u) => (
                        <PersonCard
                          key={u.userId}
                          user={u}
                          isSelf={u.userId === currentUser?.id}
                          builtIn={isBuiltIn(u)}
                          onlyOrganiser={
                            u.role === "superadmin" &&
                            !isBuiltIn(u) &&
                            explicitOrganisers <= 1
                          }
                          open={openFor === u.userId}
                          onOpen={() => openPanel(u)}
                          onClose={() => setOpenFor(null)}
                          choice={choice}
                          onChoose={chooseLevel}
                          saving={
                            setRole.isPending &&
                            setRole.variables.targetUserId === u.userId
                          }
                          error={
                            panelError?.userId === u.userId
                              ? panelError.message
                              : null
                          }
                          onSave={() => save(u)}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <InviteTeam />

      {bootstrapEmails.length > 0 ? (
        <section className="mt-12 border-t border-[color:var(--color-line)] pt-8">
          <h2 className="text-title font-bold text-[color:var(--color-ink)]">
            Built-in organisers
          </h2>
          <p className="mt-2 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
            These email addresses are always organisers, even if nobody gives
            them access here. Ask Adi to add or remove one.
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {bootstrapEmails.map((e) => (
              <li
                key={e}
                className="rounded-full border border-[color:var(--color-line-strong)] px-3 py-1 text-label text-[color:var(--color-ink)]"
              >
                {e}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

function PersonCard({
  user: u,
  isSelf,
  builtIn,
  onlyOrganiser,
  open,
  onOpen,
  onClose,
  choice,
  onChoose,
  saving,
  error,
  onSave,
}: {
  user: PrivilegedUser;
  isSelf: boolean;
  builtIn: boolean;
  /** The only organiser who isn't built in: lower levels stay closed. */
  onlyOrganiser: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  choice: AccessRole | null;
  onChoose: (value: AccessRole | null) => void;
  saving: boolean;
  error: string | null;
  onSave: () => void;
}) {
  const current = levelOf(u.role);
  const firstName = firstNameOf(u);
  const panelId = `access-panel-${u.userId}`;
  const changeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);

  // Opening or closing the panel removes the button that had focus: move
  // focus to the chosen level, or back to Change access. Closing only
  // takes focus when nothing else has it (another person's panel may have
  // just opened).
  useEffect(() => {
    if (open === wasOpen.current) {
      return;
    }
    wasOpen.current = open;
    if (open) {
      const radios = panelRef.current?.querySelectorAll<HTMLInputElement>(
        'input[type="radio"]',
      );
      const target =
        Array.from(radios ?? []).find((radio) => radio.checked) ?? radios?.[0];
      target?.focus();
    } else if (
      document.activeElement === null ||
      document.activeElement === document.body
    ) {
      changeRef.current?.focus();
    }
  }, [open]);
  const tone = current === null ? NO_ACCESS_TONE : LEVEL_TONE[current];

  const changed = choice !== current;
  const canSave = changed && !saving;
  const removing = choice === null && changed;

  return (
    <li className="surface p-4 md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-amber)]/12 text-[color:var(--color-amber)]"
          >
            <UserCog size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-body font-semibold break-words text-[color:var(--color-ink)]">
              {u.fullName ?? u.email ?? "Name not given"}
              {isSelf ? (
                <span className="ml-1.5 text-label font-normal text-[color:var(--color-ink-soft)]">
                  (you)
                </span>
              ) : null}
            </p>
            <p className="text-label break-words text-[color:var(--color-ink-soft)]">
              {u.email ?? "No email on file"}
            </p>
          </div>
        </div>
        <span
          className="badge shrink-0"
          style={
            builtIn
              ? {
                  background: "transparent",
                  color: "var(--color-ink)",
                  border: "1px solid var(--color-line-strong)",
                }
              : { background: tone.bg, color: tone.color }
          }
        >
          {builtIn
            ? BUILT_IN_ORGANISER_LABEL
            : roleDisplayLabel(current, CHAPTER.id)}
        </span>
      </div>

      {builtIn ? (
        <p className="mt-3 text-label text-[color:var(--color-ink-soft)]">
          Built in: always an organiser. Ask Adi if this needs to change.
        </p>
      ) : (
        <div className="mt-4 border-t border-[color:var(--color-line)] pt-4">
          {open ? null : (
            <button
              ref={changeRef}
              type="button"
              onClick={onOpen}
              className={`${buttonBase} border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]`}
            >
              Change access
            </button>
          )}

          {open ? (
            <div id={panelId} ref={panelRef}>
              <fieldset>
                <legend className="text-body font-semibold text-[color:var(--color-ink)]">
                  Access for {firstName}
                </legend>
                {onlyOrganiser ? (
                  <p className="mt-2 text-label text-[color:var(--color-ink-soft)]">
                    {isSelf
                      ? "You're the only organiser. Make someone else an organiser first."
                      : `${firstName} is the only organiser. Make someone else an organiser first.`}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-col gap-2">
                  {ACCESS_LEVELS.map((level) => {
                    const key = level.value ?? "none";
                    const titleId = `${panelId}-${key}-title`;
                    const descId = `${panelId}-${key}-desc`;
                    const checked = choice === level.value;
                    const disabled =
                      onlyOrganiser && level.value !== "superadmin";
                    const danger = level.value === null;
                    return (
                      <label
                        key={key}
                        className={`flex min-h-14 items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors ${
                          checked
                            ? danger
                              ? "border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/8"
                              : "border-[color:var(--color-amber)] bg-[color:var(--color-amber)]/8"
                            : "border-[color:var(--color-line)]"
                        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name={panelId}
                          checked={checked}
                          disabled={disabled}
                          onChange={() => onChoose(level.value)}
                          aria-labelledby={titleId}
                          aria-describedby={descId}
                          className="mt-1 h-5 w-5 shrink-0 accent-[color:var(--color-amber)]"
                        />
                        <span className="min-w-0">
                          <span
                            id={titleId}
                            className={`block text-body font-semibold ${
                              danger
                                ? "text-[color:var(--color-danger)]"
                                : "text-[color:var(--color-ink)]"
                            }`}
                          >
                            {roleDisplayLabel(level.value, CHAPTER.id)}
                          </span>
                          <span
                            id={descId}
                            className="mt-0.5 block text-label text-[color:var(--color-ink-soft)]"
                          >
                            {level.value === current
                              ? `${level.description} This is their access now.`
                              : level.description}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {changed ? (
                <p className="mt-4 text-body text-[color:var(--color-ink)]">
                  {choice === null
                    ? `Remove ${firstName}'s access? They won't be able to open anything.`
                    : `${firstName} ${WILL_BE[choice]}`}
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={!canSave}
                  onClick={onSave}
                  className={`${buttonBase} ${
                    removing
                      ? "bg-[color:var(--color-danger)] text-[color:var(--color-paper)]"
                      : "bg-[color:var(--color-ink)] text-[color:var(--color-paper)]"
                  } hover:opacity-85`}
                >
                  {saving
                    ? "Saving…"
                    : removing
                      ? "Yes, remove access"
                      : "Save"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={`${buttonBase} border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]`}
                >
                  {removing ? "Keep access" : "Cancel"}
                </button>
              </div>

              {error ? (
                <p
                  role="alert"
                  className="mt-3 text-label text-[color:var(--color-danger)]"
                >
                  {error}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </li>
  );
}
